import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUser } from '../context/UserContext';

const WishListSection = () => {
  const { wishlistItems: wishlistIds, removeFromWishlist, addToCart } = useUser();
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch wishlist items from API based on wishlistIds
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        
        if (wishlistIds.length === 0) {
          setWishlistProducts([]);
          setLoading(false);
          return;
        }
        
        // Fetch product details for each wishlist item
        const productPromises = wishlistIds.map(async (id) => {
          try {
            const response = await fetch(`/api/products/${id}`);
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                return data.data;
              }
            }
            return null;
          } catch (err) {
            console.error(`Error fetching product ${id}:`, err);
            return null;
          }
        });
        
        const products = await Promise.all(productPromises);
        const validProducts = products.filter(product => product !== null);
        setWishlistProducts(validProducts);
      } catch (err) {
        console.error('Error fetching wishlist:', err);
        setError('Failed to load wishlist items');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWishlist();
  }, [wishlistIds]);

  // Handled by UserContext now. We map it slightly to match the API.
  const handleAddToCart = (product) => {
    addToCart(product, 1);
    alert(`${product.name} added to cart!`);
  };

  // Format review count
  const formatReviewCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  if (loading) {
    return (
      <section className='cart py-80'>
        <div className='container container-lg'>
          <div className='text-center py-5'>
            <div className="spinner-border text-main-600" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading your wishlist...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className='cart py-80'>
        <div className='container container-lg'>
          <div className="alert alert-danger text-center" role="alert">
            {error}
          </div>
        </div>
      </section>
    );
  }

  if (wishlistProducts.length === 0) {
    return (
      <section className='cart py-80'>
        <div className='container container-lg'>
          <div className='text-center py-5'>
            <i className="ph ph-heart" style={{ fontSize: '64px', color: '#ccc' }}></i>
            <h3 className="mt-4">Your Wishlist is Empty</h3>
            <p className="text-gray-500">Save your favorite items here!</p>
            <Link to="/shop" className="btn btn-main rounded-pill px-40 py-12 mt-3">
              Start Shopping
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='cart py-80'>
      <div className='container container-lg'>
        <div className='row gy-4'>
          <div className='col-lg-12'>
            <div className='cart-table border border-gray-100 rounded-8'>
              <div className='overflow-x-auto scroll-sm scroll-sm-horizontal'>
                <table className='table rounded-8 overflow-hidden'>
                  <thead>
                    <tr className='border-bottom border-neutral-100'>
                      <th className='h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100'>
                        Product
                      </th>
                      <th className='h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100'>
                        Unit Price
                      </th>
                      <th className='h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100'>
                        Stock Status
                      </th>
                      <th className='h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100'>
                        Rating
                      </th>
                      <th className='h6 mb-0 text-lg fw-bold px-40 py-32'>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlistProducts.map((product) => (
                      <tr key={product._id}>
                        <td className='px-40 py-32 border-end border-neutral-100'>
                          <div className='table-product d-flex align-items-center gap-24'>
                            <button
                              type='button'
                              onClick={() => removeFromWishlist(product._id)}
                              className='remove-tr-btn flex-align gap-12 hover-text-danger-600'
                              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                              <i className='ph ph-x-circle text-2xl d-flex text-danger' />
                            </button>
                            <Link
                              to={`/product-details/${product._id}`}
                              className='table-product__thumb border border-gray-100 rounded-8 flex-center'
                              style={{
                                width: '80px',
                                height: '80px',
                                overflow: 'hidden',
                                borderRadius: '8px'
                              }}
                            >
                              <img
                                src={product.image || 'assets/images/thumbs/product-two-img1.png'}
                                alt={product.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.src = 'assets/images/thumbs/product-two-img1.png';
                                }}
                              />
                            </Link>
                            <div className='table-product__content text-start'>
                              <h6 className='title text-lg fw-semibold mb-8'>
                                <Link
                                  to={`/product-details/${product._id}`}
                                  className='link text-line-2'
                                  style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                  {product.name}
                                </Link>
                              </h6>
                              <div className='flex-align gap-16 mb-16'>
                                <div className='flex-align gap-6'>
                                  <span className='text-md fw-medium text-warning-600 d-flex'>
                                    <i className='ph-fill ph-star' />
                                  </span>
                                  <span className='text-md fw-semibold text-gray-900'>
                                    {product.rating || 4.7}
                                  </span>
                                </div>
                                <span className='text-sm fw-medium text-gray-200'>
                                  |
                                </span>
                                <span className='text-neutral-600 text-sm'>
                                  {formatReviewCount(product.reviewsCount || 0)} Reviews
                                </span>
                              </div>
                              <div className='flex-align gap-16 flex-wrap'>
                                <span className='badge bg-light text-dark px-12 py-6 rounded-pill'>
                                  {product.category || 'Plants'}
                                </span>
                                <span className='badge bg-light text-dark px-12 py-6 rounded-pill'>
                                  {product.plantType || 'Indoor'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className='px-40 py-32 border-end border-neutral-100'>
                          <span className='text-lg h6 mb-0 fw-semibold'>
                            ₹{product.price?.toFixed(2) || '0.00'}
                          </span>
                          {product.originalPrice && product.originalPrice > product.price && (
                            <span className='text-gray-400 text-sm fw-semibold text-decoration-line-through d-block'>
                              ₹{product.originalPrice.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className='px-40 py-32 border-end border-neutral-100'>
                          <span className={`text-lg h6 mb-0 fw-semibold ${product.stock > 0 ? 'text-success' : 'text-danger'}`}>
                            {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className='px-40 py-32 border-end border-neutral-100'>
                          <div className='flex-align gap-6'>
                            <span className='text-md fw-medium text-warning-600 d-flex'>
                              <i className='ph-fill ph-star' />
                            </span>
                            <span className='text-md fw-semibold text-gray-900'>
                              {product.rating || 4.7}
                            </span>
                            <span className='text-neutral-600 text-sm'>
                              ({formatReviewCount(product.reviewsCount || 0)})
                            </span>
                          </div>
                        </td>
                        <td className='px-40 py-32'>
                          <button
                            onClick={() => handleAddToCart(product)}
                            className='btn btn-main-two rounded-8 px-40 py-12'
                            disabled={product.stock === 0}
                            style={{
                              opacity: product.stock === 0 ? 0.5 : 1,
                              cursor: product.stock === 0 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <i className='ph ph-shopping-cart' /> 
                            {product.stock > 0 ? 'Add To Cart' : 'Out of Stock'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .remove-tr-btn {
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .remove-tr-btn:hover {
          color: #dc2626 !important;
        }
        .table-product__thumb {
          transition: transform 0.3s ease;
        }
        .table-product__thumb:hover {
          transform: scale(1.05);
        }
        @media (max-width: 768px) {
          .px-40 {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .py-32 {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default WishListSection;